import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Table,
  Row,
  Col,
  Alert,
} from "reactstrap";
import { useLazyQuery, useQuery } from "@apollo/client";
import ReactMoment from "react-moment";
import NumberFormat from "react-number-format";
import { toast } from "react-toastify";
import {
  SETTLEMENT_LIST_QUERY,
  CODE_BRANCH_LIST_QUERY,
} from "../../config/Queries";

const getLastMonth = () => {
  const now = new Date();
  const last = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return { year: last.getFullYear(), month: last.getMonth() + 1 };
};

const formatPhone = (phone) => {
  if (!phone || phone.length < 10) return phone || "-";
  return (
    phone.substr(0, 3) + "-" + phone.substr(3, 4) + "-" + phone.substr(7, 4)
  );
};

const formatAmount = (value) => {
  if (value == null) return "0";
  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

function SettlementList() {
  const printRef = useRef(null);
  const defaultPeriod = getLastMonth();
  const currentYear = new Date().getFullYear();

  const [year, setYear] = useState(defaultPeriod.year);
  const [month, setMonth] = useState(defaultPeriod.month);
  const [branchCode, setBranchCode] = useState("");

  const { data: branchData } = useQuery(CODE_BRANCH_LIST_QUERY);

  const [fetchSettlement, { data, loading, called }] = useLazyQuery(
    SETTLEMENT_LIST_QUERY,
    { fetchPolicy: "network-only" }
  );

  const runSearch = (searchYear, searchMonth, searchBranchCode) => {
    const now = new Date();
    if (
      searchYear === now.getFullYear() &&
      searchMonth === now.getMonth() + 1
    ) {
      toast.warning("아직 정산이 진행중입니다.", {
        autoClose: 3000,
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    fetchSettlement({
      variables: {
        year: searchYear,
        month: searchMonth,
        branchCode: searchBranchCode ? parseInt(searchBranchCode) : null,
      },
    });
  };

  useEffect(() => {
    runSearch(defaultPeriod.year, defaultPeriod.month, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    runSearch(year, month, branchCode);
  };

  const settlement = data?.listSettlement;
  const items = settlement?.items || [];
  const selectedBranchLabel =
    branchCode === ""
      ? "전체"
      : branchData?.listCodeBranch?.find(
          (b) => String(b.code) === String(branchCode)
        )?.branchName || "전체";

  const handlePrint = () => {
    if (!items.length) {
      toast.warning("인쇄할 데이터가 없습니다.", {
        autoClose: 3000,
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }
    window.print();
  };

  const handleExcelDownload = () => {
    if (!items.length) {
      toast.warning("다운로드할 데이터가 없습니다.", {
        autoClose: 3000,
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    const headers = [
      "연번",
      "공고번호",
      "지점",
      "간병인",
      "간병인 연락처",
      "환자명",
      "공고 제목",
      "간병 기간",
      "입금금액",
      "입금완료일",
    ];

    const rows = items.map((item, index) => [
      items.length - index,
      item.code,
      item.branchName,
      item.caregiverName,
      formatPhone(item.caregiverPhone),
      item.patientName,
      item.title,
      `${item.startDate} ~ ${item.endDate}`,
      item.confirmCost,
      item.completedAt.substr(0, 10),
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) =>
        row
          .map((cell) => `"${String(cell).replace(/"/g, '""')}"`)
          .join(",")
      ),
      "",
      `"총 건수","${settlement.count}"`,
      `"총 금액","${settlement.totalAmount}"`,
    ].join("\n");

    const blob = new Blob(["\uFEFF" + csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `정산_${year}년_${month}월_${selectedBranchLabel}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const yearOptions = [];
  for (let y = currentYear; y >= currentYear - 5; y--) {
    yearOptions.push(y);
  }

  return (
    <>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .main-panel { width: 100% !important; float: none !important; }
          .sidebar, .navbar, footer { display: none !important; }
          .print-area { padding: 0 !important; }
          table { font-size: 11px; }
        }
      `}</style>
      <div className="content print-area" ref={printRef}>
        <Row>
          <Col md="12">
            <Card>
              <CardHeader className="no-print">
                <CardTitle tag="h4">정산관리</CardTitle>
              </CardHeader>
              <CardBody>
                <div className="no-print m-b-20">
                  <Row className="align-items-end">
                    <Col xs="12" sm="3" className="m-b-10">
                      <label className="control-label">정산 연도</label>
                      <select
                        className="form-control"
                        value={year}
                        onChange={(e) => setYear(parseInt(e.target.value))}
                      >
                        {yearOptions.map((y) => (
                          <option key={y} value={y}>
                            {y}년
                          </option>
                        ))}
                      </select>
                    </Col>
                    <Col xs="12" sm="3" className="m-b-10">
                      <label className="control-label">정산 월</label>
                      <select
                        className="form-control"
                        value={month}
                        onChange={(e) => setMonth(parseInt(e.target.value))}
                      >
                        {Array.from({ length: 12 }, (_, i) => i + 1).map(
                          (m) => (
                            <option key={m} value={m}>
                              {m}월
                            </option>
                          )
                        )}
                      </select>
                    </Col>
                    <Col xs="12" sm="3" className="m-b-10">
                      <label className="control-label">지점</label>
                      <select
                        className="form-control"
                        value={branchCode}
                        onChange={(e) => setBranchCode(e.target.value)}
                      >
                        <option value="">전체</option>
                        {branchData?.listCodeBranch?.map((branch) => (
                          <option key={branch.code} value={branch.code}>
                            {branch.branchName}
                          </option>
                        ))}
                      </select>
                    </Col>
                    <Col xs="12" sm="3" className="m-b-10">
                      <Button color="primary" block onClick={handleSearch}>
                        조회
                      </Button>
                    </Col>
                  </Row>
                  <Row className="m-t-10">
                    <Col>
                      <Button
                        color="default"
                        className="m-r-10"
                        onClick={handlePrint}
                      >
                        <i className="fa fa-print" /> 인쇄
                      </Button>
                      <Button color="success" onClick={handleExcelDownload}>
                        <i className="fa fa-download" /> 엑셀 다운로드
                      </Button>
                    </Col>
                  </Row>
                </div>

                <div className="m-b-15">
                  <h5 className="mb-1">
                    {year}년 {month}월 · {selectedBranchLabel}
                  </h5>
                  <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                    입금완료 기준 · 입금확인 처리일 기준 집계
                  </p>
                </div>

                {settlement?.error && (
                  <Alert color="warning">{settlement.error}</Alert>
                )}

                {called && !loading && settlement?.result && (
                  <Row className="m-b-20">
                    <Col xs="6" sm="4">
                      <strong>총 건수</strong>
                      <div style={{ fontSize: 20 }}>
                        {settlement.count.toLocaleString()}건
                      </div>
                    </Col>
                    <Col xs="6" sm="4">
                      <strong>총 입금금액</strong>
                      <div style={{ fontSize: 20 }}>
                        <NumberFormat
                          value={settlement.totalAmount}
                          displayType="text"
                          thousandSeparator
                          suffix="원"
                        />
                      </div>
                    </Col>
                  </Row>
                )}

                {!loading && called && settlement?.result && (
                  <Table responsive bordered hover>
                    <thead className="text-primary">
                      <tr>
                        <th>연번</th>
                        <th>지점</th>
                        <th>간병인</th>
                        <th>간병인 연락처</th>
                        <th>환자명</th>
                        <th>공고 제목</th>
                        <th>간병 기간</th>
                        <th>입금금액</th>
                        <th>입금완료일</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.length === 0 && (
                        <tr>
                          <td colSpan="9" className="text-center">
                            조회된 데이터가 없습니다.
                          </td>
                        </tr>
                      )}
                      {items.map((item, index) => (
                        <tr key={item.code}>
                          <td>{items.length - index}</td>
                          <td>{item.branchName}</td>
                          <td>{item.caregiverName}</td>
                          <td>{formatPhone(item.caregiverPhone)}</td>
                          <td>{item.patientName}</td>
                          <td>{item.title}</td>
                          <td>
                            {item.startDate} ~ {item.endDate}
                          </td>
                          <td>
                            <NumberFormat
                              value={item.confirmCost}
                              displayType="text"
                              thousandSeparator
                              suffix="원"
                            />
                          </td>
                          <td>
                            <ReactMoment format="YYYY.MM.DD">
                              {parseInt(item.completedAt)}
                            </ReactMoment>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    {items.length > 0 && (
                      <tfoot>
                        <tr>
                          <td colSpan="7" className="text-right">
                            <strong>합계</strong>
                          </td>
                          <td colSpan="2">
                            <strong>
                              {settlement.count}건 /{" "}
                              {formatAmount(settlement.totalAmount)}원
                            </strong>
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </Table>
                )}

                {loading && (
                  <p className="text-center text-muted">조회 중...</p>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
}

export default SettlementList;
